import type { AutoQaRatingCategory } from '@zendesk/zqa-services/autoqa'
import type { Rating as RatingTypeFromServices, RatingRootCause } from '@zendesk/zqa-services/reviews'
import type {
  AdvancedScorecard_RootCauses as RootCauses,
  AdvancedScorecard_ScorecardRatingCategoryRating as CategoryRating,
} from '@zendesk/zqa-services/scorecards'
import { type Scale, type ScaleType, SCALE_VALUES } from '@/data/scales'
import type { Scorecard } from '@/modules/shared/TicketContent/types/scorecard'
import { type RootCause, RequireReasonVisibility, type AutoQaCategoryType } from '@/types/category'

/**
 * This utils module should house functional methods to better unify and
 * re-use the different ratings and grouping methods used in:
 *
 * Feedback.vue
 * ReviewSidebar.vue (TicketContent)
 * Review.vue (extension)
 */

// Golang services types
export interface Category {
  categoryDescription?: string
  categoryId: number
  categoryName: string
  critical: boolean
  reviewCritical: boolean
  groupCritical: boolean
  groupId?: number | string
  groupName?: string
  groupPosition?: number
  isArchived: boolean
  multipleRequireReasons: boolean
  position: number
  requireReason: boolean
  requireReasonVisibility: RequireReasonVisibility
  autoQaApplicabilities: string[]
  freeTextAllowed: boolean
  rootCauses: RootCause[]
  scale: ScaleType
  scorecards: Scorecard[]
  weight: number
  autoQaCategory?: AutoQaCategoryType
  autoQaCustomCategoryTemplateId?: string
  autoQaRatingCategory?: AutoQaRatingCategory
  advancedRootCauses?: RootCauses
  skippable: boolean
  isAdvancedScorecardCategory?: boolean
  scorecardId?: string
  categoryRatings: CategoryRating[]
  status?: string
  evaluated?: boolean
  skipReason?: string
}

export interface Rating extends Category {
  rating?: number
  cause?: string[] | null
  rootCause?: RatingRootCause
  failCategory?: boolean
  scorecardId?: string
}

export interface RatingRequestPayload {
  categoryId: number
  rating: number | undefined
  cause?: string[] | null
  rootCause?: RatingRootCause
}

export interface CategoryGroup {
  id: number
  name: string
  position: number
  categories: Rating[]
}

export type GivenRatings = Record<string, Rating>

export type RatingFields = Pick<Rating, 'categoryId' | 'cause' | 'rootCause'> & { rating?: number | null }

interface DraftRatingFields extends Omit<RatingFields, 'cause' | 'rating'> {
  cause: string[]
  rating?: number
}

// This value is used to mark "N/A" ratings if the Account-level Scale settings allow it
export const RATING_NA = 42

export const getScorecardId = (scorecard?: Scorecard | string) =>
  typeof scorecard === 'string' ? scorecard : scorecard?.id

export const populateWithNA = (givenRatings: GivenRatings): Rating[] =>
  Object.values(givenRatings).map((r) => {
    if (r.skippable) {
      return { ...r, rating: r.rating ?? RATING_NA }
    } else {
      return r
    }
  })

/**
 * Keeps selected rating values when the same review context rebuilds category metadata.
 * Nullish rating values mean "not rated" and are not copied; other runtime values are preserved.
 */
export const preserveExistingRatingValues = <T extends Record<number, RatingFields>>(
  blankRatings: T,
  previousRatings: Record<number, RatingFields>,
): T => {
  return Object.fromEntries(
    Object.entries(blankRatings).map(([categoryId, rating]) => {
      const previousRating = previousRatings[Number(categoryId)]

      if (previousRating?.rating == null) return [categoryId, rating]

      return [
        categoryId,
        {
          ...rating,
          rating: previousRating.rating,
          cause: previousRating.cause,
          rootCause: previousRating.rootCause,
        },
      ]
    }),
  ) as T
}

/**
 * Drafts can contain ratings from scorecards that are not currently visible.
 * Keep those previous values while allowing current scorecard categories to overwrite or clear their own values.
 */
export const mergeDraftRatingValues = (currentRatings: RatingFields[], previousDraftRatings: RatingFields[]) => {
  const currentRatingCategoryIds = new Set(currentRatings.map((rating) => rating.categoryId))
  const currentRatedRatings = currentRatings.filter((rating) => rating.rating != null)
  const preservedRatings = previousDraftRatings.filter(
    (rating) => rating.rating != null && !currentRatingCategoryIds.has(rating.categoryId),
  )

  return [...currentRatedRatings, ...preservedRatings]
}

export const shouldRemoveReviewDraft = ({
  hasComment,
  canReview,
  hasDraftRatings,
  hasGivenRatings = hasDraftRatings,
}: {
  hasComment: boolean
  canReview: boolean
  hasDraftRatings: boolean
  hasGivenRatings?: boolean
}) => !hasComment && (!canReview || !hasDraftRatings || !hasGivenRatings)

export const groupCategories = <T extends { id: number; name: string; position: number }>(
  categories: Rating[],
  groups: T[],
  keepEmptyGroups = false,
): CategoryGroup[] => {
  const ungroupedCategories = categories
    .filter((cat) => !cat.groupId)
    .sort((a, b) => (a.position || 0) - (b.position || 0))
  return [
    getUngroupedCategoryGroup(ungroupedCategories),
    ...groups
      .map((group) => ({
        ...group,
        categories: categories
          .filter((cat) => cat.groupId === group.id)
          .sort((a, b) => (a.position || 0) - (b.position || 0)),
      }))
      .filter((group) => keepEmptyGroups || group.categories.length),
  ]
}

export function buildRatingsMap({
  ratings,
  categoryOptions,
  preferRatingValues,
}: {
  ratings: RatingTypeFromServices[]
  categoryOptions: Record<number, Category>
  preferRatingValues: boolean
}): GivenRatings {
  return ratings.reduce((ratingsMap, rating) => {
    const category = categoryOptions[rating.categoryId] as Category | undefined

    if (!category) return ratingsMap
    const commonProperties = {
      scale: `SCALE_${rating.ratingMax + 1}`,
      critical: rating.critical,
      rating: rating.rating,
      cause: rating.cause,
      rootCause: rating.rootCause,
    }

    // if preferRatingValues = true -> return category config that was used while rating and adds the rating values
    // if preferRatingValues = false -> returns the latest category config (updated descriptions, root causes, etc.) and adds the rating values
    if (preferRatingValues) {
      ratingsMap[rating.categoryId] = {
        ...category,
        ...commonProperties,
      }
    } else {
      ratingsMap[rating.categoryId] = {
        ...commonProperties,
        ...category,
      }
    }

    return ratingsMap
  }, {})
}

export const toRatingRequestPayload = (
  { categoryId, rating, cause, rootCause }: Rating,
  includeRootCause = false,
): RatingRequestPayload =>
  includeRootCause
    ? {
        categoryId,
        rating,
        ...(rootCause ? { rootCause } : {}),
        cause,
      }
    : {
        categoryId,
        rating,
        cause,
      }

export const isRated = (r?: { rating?: number | null }) => r && ![null, undefined, RATING_NA].includes(r.rating)

export const isValidComment = (comment: string) => {
  const content = comment.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '').replace(/&#39;/g, `'`)
  return !!content.trim().length
}

export const hasRootCauses = (cause: string[] | undefined) => !!cause?.length

export const hasSelectableRootCauseId = (id: unknown) =>
  id !== undefined && id !== null && String(id).trim().length > 0

export const isNegativeRating = (rating: number, scale: Scale[]) => rating < Math.floor((scale?.length ?? 0) / 2)

export const isNeutralRating = (rating: number, scale: Scale[]) => {
  if (scale.length % 2 !== 1) {
    // Array does not have a neutral rating (i.e. it's either a binary or 4-point scale)
    return false
  }

  const neutralRatingIndex = Math.floor(scale.length / 2)
  const neutralRating = scale[neutralRatingIndex].label - 1

  return neutralRating === rating
}

export const isRootCauseRequiredForRating = (rating: number, ratingCategory: Rating): boolean => {
  const { requireReason, requireReasonVisibility, scale, isAdvancedScorecardCategory } = ratingCategory

  if (!requireReason) return false
  if (isAdvancedScorecardCategory) return isReasonRequiredForRating(rating, ratingCategory.advancedRootCauses)

  // legacy category
  if (requireReasonVisibility === RequireReasonVisibility.All) return true

  if (requireReasonVisibility === RequireReasonVisibility.NegativeAndNeutral) {
    return isNegativeRating(rating, SCALE_VALUES[scale]) || isNeutralRating(rating, SCALE_VALUES[scale])
  }

  return isNegativeRating(rating, SCALE_VALUES[scale])
}

export const isReasonRequiredForRatingLegacy = (
  rating: number,
  scale: Scale[],
  requireReasonVisibility: RequireReasonVisibility,
): boolean => {
  switch (requireReasonVisibility) {
    case RequireReasonVisibility.All:
      return true
    case null:
    case RequireReasonVisibility.Negative: {
      return isNegativeRating(rating, scale!)
    }
    case RequireReasonVisibility.NegativeAndNeutral: {
      const negativeRating = isNegativeRating(rating, scale!)
      const neutralRating = isNeutralRating(rating, scale!)
      return negativeRating || neutralRating
    }
    default:
      return false
  }
}

const getRootCausesRating = (rating: number, rootCauses: RootCauses | undefined) => {
  if (rating === null) return undefined

  const rootCausesRating = rootCauses?.ratings.find((r) => r.rating === rating)
  if (!rootCausesRating) return undefined

  return rootCausesRating
}

type RootCausesRating = RootCauses['ratings'][number]
type MultiTierRootCause = NonNullable<RootCausesRating['multiTierRootCause']>
type MultiTierRootCauseTier = MultiTierRootCause['tiers'][number]
interface NamedRootCause {
  id?: number | string
  name?: string
  cause?: string
}
interface ConfiguredRootCause {
  id?: number | string
  cause: string
}

const getRootCauseLabel = (rootCause: NamedRootCause) => rootCause.name?.trim() || rootCause.cause?.trim()
const isRootCauseLabel = (label: string | undefined): label is string => !!label
const isConfiguredRootCause = (rootCause: ConfiguredRootCause | undefined): rootCause is ConfiguredRootCause =>
  !!rootCause

const toConfiguredRootCause = (rootCause: NamedRootCause): ConfiguredRootCause | undefined => {
  const cause = getRootCauseLabel(rootCause)

  return cause ? { id: rootCause.id, cause } : undefined
}

const getTierRootCauseOptions = (tiers: MultiTierRootCauseTier[]): ConfiguredRootCause[] =>
  tiers.flatMap((tier) => [
    ...(tier.rootCauses || []).map(toConfiguredRootCause).filter(isConfiguredRootCause),
    ...getTierRootCauseOptions(tier.subtiers || []),
  ])

const getConfiguredRootCauseOptions = (rootCausesRating: RootCausesRating): ConfiguredRootCause[] => [
  ...(rootCausesRating.rootCauses || []).map(toConfiguredRootCause).filter(isConfiguredRootCause),
  ...(rootCausesRating.multiTierRootCause?.looseRootCauses || [])
    .map(toConfiguredRootCause)
    .filter(isConfiguredRootCause),
  ...getTierRootCauseOptions(rootCausesRating.multiTierRootCause?.tiers || []),
]

const getConfiguredRootCauseLabels = (rootCausesRating: RootCausesRating) =>
  getConfiguredRootCauseOptions(rootCausesRating).map(({ cause }) => cause)

export const getReasonStringsFromRootCause = (rootCause?: RatingRootCause) => {
  if (!rootCause) return []

  const predefinedReasons = (rootCause.predefined || []).map(({ cause }) => cause)
  const otherReason = rootCause.other?.trim()
  const otherReasons = otherReason ? [otherReason] : []

  return [...predefinedReasons, ...otherReasons].filter(isRootCauseLabel)
}

export const getConfiguredRootCauseLabelsForRating = (rating: number, rootCauses: RootCauses | undefined): string[] => {
  const rootCausesRating = getRootCausesRating(rating, rootCauses)

  return rootCausesRating ? getConfiguredRootCauseLabels(rootCausesRating) : []
}

export const getRootCausesForRating = (rating: number, rootCauses: RootCauses) => {
  return getRootCausesRating(rating, rootCauses)?.rootCauses || []
}

interface RatingRootCauseDerivationSource {
  rating?: number | null
  cause?: string[] | null
  advancedRootCauses?: RootCauses
  freeTextAllowed?: boolean
}

interface DraftRatingSource extends RatingRootCauseDerivationSource {
  categoryId: number
  rootCause?: RatingRootCause
}

export const deriveRatingRootCauseFromCause = ({
  rating,
  cause,
  advancedRootCauses,
  freeTextAllowed = false,
}: RatingRootCauseDerivationSource): RatingRootCause | undefined => {
  if (typeof rating !== 'number' || !cause?.length) return undefined

  const rootCausesRating = getRootCausesRating(rating, advancedRootCauses)
  if (!rootCausesRating) return undefined

  const configuredRootCauseOptions = getConfiguredRootCauseOptions(rootCausesRating)
  const configuredRootCauseLabels = new Set(configuredRootCauseOptions.map(({ cause }) => cause))
  const configuredRootCausesByCause = new Map<string, { id: string; cause: string }[]>()

  configuredRootCauseOptions.forEach((rootCause) => {
    if (!hasSelectableRootCauseId(rootCause.id)) return

    const configuredRootCause = {
      id: String(rootCause.id),
      cause: rootCause.cause,
    }

    configuredRootCausesByCause.set(rootCause.cause, [
      ...(configuredRootCausesByCause.get(rootCause.cause) || []),
      configuredRootCause,
    ])
  })

  const predefined: RatingRootCause['predefined'] = []
  const predefinedIds = new Set<string>()
  const otherRootCauses: string[] = []
  const otherRootCauseLabels = new Set<string>()
  const canSaveOtherRootCause =
    freeTextAllowed || !!advancedRootCauses?.freeTextAllowed || !!rootCausesRating.freeTextAllowed

  cause.forEach((selectedRootCause) => {
    const selectedCause = selectedRootCause.trim()
    if (!selectedCause) return

    const configuredRootCauses = configuredRootCausesByCause.get(selectedCause) || []
    if (configuredRootCauses.length === 1) {
      const [configuredRootCause] = configuredRootCauses

      if (predefinedIds.has(configuredRootCause.id)) return

      predefinedIds.add(configuredRootCause.id)
      predefined.push(configuredRootCause)
      return
    }

    const hasAmbiguousConfiguredRootCauseLabel = configuredRootCauses.length > 1
    const shouldSkipOtherRootCause =
      !canSaveOtherRootCause ||
      (configuredRootCauseLabels.has(selectedCause) && !hasAmbiguousConfiguredRootCauseLabel)

    if (shouldSkipOtherRootCause) return
    if (otherRootCauseLabels.has(selectedCause)) return

    otherRootCauseLabels.add(selectedCause)
    otherRootCauses.push(selectedCause)
  })

  if (!predefined.length && !otherRootCauses.length) return undefined

  return {
    predefined,
    other: otherRootCauses.join('; '),
  }
}

export const normalizeDraftRatingCause = (cause?: string[] | null): string[] => cause ?? []

export const toDraftRatingFields = (rating: DraftRatingSource): DraftRatingFields => {
  const cause = normalizeDraftRatingCause(rating.cause)
  const rootCause = rating.rootCause ?? deriveRatingRootCauseFromCause({ ...rating, cause })

  return {
    cause,
    ...(rootCause !== undefined ? { rootCause } : {}),
    rating: rating.rating ?? undefined,
    categoryId: rating.categoryId,
  }
}

export const isReasonRequiredForRating = (rating: number, rootCauses: RootCauses | undefined): boolean => {
  if (!rootCauses) return false

  const rootCausesForRating = getRootCausesRating(rating, rootCauses)

  if (!rootCausesForRating) return false

  return getConfiguredRootCauseLabels(rootCausesForRating).length > 0 || !!rootCausesForRating.freeTextAllowed
}

export const getUngroupedCategoryGroup = (categories: Category[]): CategoryGroup => ({
  id: 0,
  name: 'Ungrouped',
  position: 0,
  categories,
})

// if it's legacy, it will return the causes as they are
// if freeTextAllowed is true, it will return the causes as they are
// if freeTextAllowed is false, it will filter out any cause that is not in the category's advanced root causes
export const getValidRootCauses = (
  rating: number,
  causes: string[] | undefined,
  advancedRootCauses: RootCauses | undefined,
  freeTextAllowed = false,
  legacy = true,
): string[] => {
  if (!causes || causes.length === 0) return []
  if (legacy || freeTextAllowed) {
    return causes
  }

  const rootCausesForRating = getRootCausesRating(rating, advancedRootCauses)

  if (!rootCausesForRating) return []
  if (rootCausesForRating.freeTextAllowed) return causes

  const existingRootCauses = getConfiguredRootCauseLabels(rootCausesForRating)
  return causes.filter((c: string) => existingRootCauses.includes(c))
}
